/**
 * PARCHE CABSEN (2026-10-09): aviso de perdida del contexto WebGL del visor.
 *
 * En los telefonos el navegador puede quitarle al visor su contexto WebGL (la memoria grafica) en plena
 * carga. cornerstone/vtk no se recuperan: el siguiente render lanza un TypeError dentro de
 * requestAnimationFrame (vtk.js, Rendering/OpenGL/ImageMapper: el programa del shader es null y se lee
 * `isAttributeUsed`), el motor deja de pintar y la imagen queda en negro, con el aviso rojo generico encima.
 * En vez de eso se muestra "El visor necesita recargarse" con un boton para recargar
 * (extensions/default/src/ViewerLayout/AvisoContextoPerdido.tsx).
 *
 * El estado viaja en un CustomEvent de window para no acoplar los tres sitios implicados:
 *  - extensions/cornerstone/src/utils/vigilarContextoWebGL.ts: 'webglcontextlost' en los canvas del pool
 *    del motor de cornerstone (no en los canvas de sondeo, que pierden el contexto a proposito).
 *  - components/Errorboundary/ErrorBoundary.tsx: el TypeError con la firma de vtk.
 *  - AvisoContextoPerdido (en los dos layouts del visor): escucha el evento y pinta el aviso.
 */
export const EVENTO_CONTEXTO_PERDIDO = 'cabsen:contexto-perdido';

export type OrigenContextoPerdido = 'webglcontextlost' | 'error';

export interface DetalleContextoPerdido {
  origen: OrigenContextoPerdido;
}

/** Nombre del metodo de vtk que aparece en el TypeError (Chrome: "reading 'isAttributeUsed'"; Safari:
 * "evaluating '...getProgram().isAttributeUsed'"). Filtro estrecho: cualquier otro error sigue su curso. */
const FIRMA_ERROR_VTK = 'isAttributeUsed';

/** true si el error (Error, ErrorEvent.message o texto) es el de vtk por perdida del contexto WebGL. */
export function esErrorDeContextoPerdido(error: unknown): boolean {
  try {
    if (!error) {
      return false;
    }
    if (typeof error === 'string') {
      return error.includes(FIRMA_ERROR_VTK);
    }
    const { message, stack } = error as { message?: unknown; stack?: unknown };
    return [message, stack].some(
      texto => typeof texto === 'string' && texto.includes(FIRMA_ERROR_VTK)
    );
  } catch (e) {
    return false;
  }
}

/** Avisa (una o mas veces, es idempotente para quien escucha) de que el visor perdio su contexto WebGL. */
export function avisarContextoPerdido(origen: OrigenContextoPerdido, causa?: unknown): void {
  try {
    console.warn(
      `[cabsen] El visor perdio su contexto WebGL (${origen}); se muestra el aviso para recargar.`,
      causa ?? ''
    );
    window.dispatchEvent(
      new CustomEvent<DetalleContextoPerdido>(EVENTO_CONTEXTO_PERDIDO, { detail: { origen } })
    );
  } catch (e) {
    // El aviso es una ayuda: si algo falla aqui no debe romper nada mas.
  }
}
